<template>
  <div data-view="monthly-reports">
    <layout-logged-frame
      title="Billing"
      has-aside
    >
      <template v-slot:before-content>
        <div class="full mb-4">
          <MonthlyFilter
            :is-skeleton-loading="isSkeletonLoading"
            ref="dealsfilter"
            @filter="onFilter"
            @clearFilters="onFilter"
          >
            <template v-slot:phone-actions>
              <sort-by-select
                :columns="invoiceColumnsSorted"
                :pagination="pagination"
                :on-update-pagination="onUpdatePagination"
              />
            </template>
          </MonthlyFilter>
        </div>
      </template>

      <template v-slot:aside>
        <RunningInvoices @onChangeLoadingStatus="onChangeLoadingStatus"/>
      </template>

      <template v-slot:content>
        <div class="form-wrapper wide">
          <table-base
            has-phone-layout
            :columns="invoiceColumnsSorted"
            :items="invoices"
            :pages="totalPages"
            :pagination="pagination"
            has-custom-items-template
            :no-data-title="$t('no_bills')"
            :no-data-subtitle="$t('start_to_use_our_products')"
            :can-edit-columns="false"
            :is-loading="isLoading"
            :on-update-pagination="onUpdatePagination"
            :on-change-page="onChangePage"
          >
            <template v-slot:title>
              <div class="monthly-reports__title">
                {{ $t('generated_invoices') }}
              </div>
            </template>

            <template
              v-slot:item-cell="scope"

            >
              <template v-if="scope.cell.column.value === 'action'">
                <div
                  v-if="scope.cell.item.has_pdf"
                  class="trigger download-invoice"
                  @click="() => downloadInvoice(scope.cell.item)"
                >
                  <v-icon>
                    {{ scope.cell.column.icon }}
                  </v-icon>
                </div>

                <span v-else class="no-pdf">&mdash;</span>
              </template>

              <template v-else-if="scope.cell.column.value === 'total_amount'">
                <div class="amount">
                  {{ `${scope.cell.item.total_amount} ${scope.cell.item.cost_center.currency.symbol}` }}
                </div>
              </template>

              <template v-else>
                {{ displayColumnValue(scope.cell.item, scope.cell.column.value) }}
              </template>
            </template>
          </table-base>
        </div>
      </template>
    </layout-logged-frame>
  </div>
</template>

<script>
  import MonthlyFilter from '../../components/forms/filters/Monthly';
  import invoiceColumns from '../../config/reports/invoice/columns';
  import RunningInvoices from '../../components/panels/RunningInvoices';
  import { isStr, extractNestedProp } from '@/utils';

  export default {
    components: {
      MonthlyFilter,
      RunningInvoices
    },
    data() {
      return {
        invoiceColumns,
        contracts: [],
        invoices: [],
        query: {},
        totalPages: 0,
        pagination: {
          order: 'desc' ,
          page: 1,
          rowsPerPage: 15,
          sortBy: 'issue_date',
          totalItems: 0,
          search: ''
        },
        isLoading: false,
        isLoadingRunningInvoices: true,
        isSkeletonLoading: true,
        show_billing: true,
        show_country: true,
      };
    },
    computed: {
      invoiceColumnsSorted() {
        const sortedInvoiceColumns = this.invoiceColumns;

        if (!this.show_billing) {
          const idx = sortedInvoiceColumns.findIndex(el => el.value === 'legal_business_name');
          if (idx >= 0) sortedInvoiceColumns.splice(idx, 1);
        }
        if (!this.show_country) {
          const idx = sortedInvoiceColumns.findIndex(el => el.value === 'cost_center.name');
          if (idx >= 0) sortedInvoiceColumns.splice(idx, 1);
        }

        return sortedInvoiceColumns;
      },
      billingAccounts() {
        return this.$store.getters['invoices/accounts'];
      },
    },
    methods: {
      onChangeLoadingStatus() {
        this.isLoadingRunningInvoices = false;
      },
      getInvoices({ status = 1, page = 1, sort, order = 'asc' } = {}) {
        const query = {
          ...this.query,
          status,
          page,
          sort,
          order,
          per_page: 15,
        };

        this.isLoading = true;

        return this.$store.dispatch('reports/getInvoices', query)
          .then(({ data, meta }) => {
            const { total, current_page, last_page, show_billing, show_country } = meta;
            this.invoices = data;
            this.pagination.page = current_page;
            this.pagination.totalItems = total;
            this.totalPages = last_page;
            this.show_billing = show_billing;
            this.show_country = show_country;
            this.isLoading = false;
            this.isSkeletonLoading = false;

            return data;
          })
          .catch((err) => {
            this.isLoading = false;
            this.isSkeletonLoading = false;
            return err;
          });
      },
      displayColumnValue(item, columnValue) {
        return extractNestedProp(item, columnValue);
      },
      downloadInvoice(invoice) {
        const query = {
          id: invoice.id
        };

        return this.$store.dispatch('invoices/downloadInvoice', query).catch((err) => err);
      },
      onUpdatePagination({ page, order, sortBy }) {
        const { query, pagination } = this;
        this.pagination.order = order;
        this.pagination.sortBy = isStr(sortBy) ? sortBy : pagination.sortBy;

        const nextQuery = {
          ...query,
          page,
          order,
          sort: isStr(sortBy) ? sortBy : pagination.sortBy
        };

        this.query = nextQuery;

        this.getInvoices(nextQuery);
      },
      onChangePage(page) {
        const { query, pagination } = this;
        const { order, sortBy } = pagination;
        const nextQuery = {
          ...query,
          page,
          order,
          sort: sortBy
        };

        this.query = nextQuery;

        this.getInvoices(nextQuery);
      },
      onFilter(filters) {
        this.query = {
          ...this.query,
          ...this.getSearchQuery(filters),
          page: 1
        };

        this.getInvoices(this.query);
      },
      getSearchQuery({ search, issueDate, invoicePeriod, countries }) {
        const query = {};

        query.search = search;
        query.created_at = issueDate;
        query.invoice_period = invoicePeriod;
        query.countries = countries;
        query.page = this.query.page;

        return query;
      },
    },
  };
</script>

<style lang="scss">
  @use '../../assets/styles/theme' as *;

  [data-view="monthly-reports"] {
    // .trigger.download-paid-invoice,
    .amount {
      white-space: nowrap;
      text-align: right;
      margin: 0 20px 0 0;
    }

    .monthly-reports__title {
      font-size: 18px;
      font-weight: 700;
    }

    .no-pdf {
      display: block;
      text-align: center;
      color: rgba(var(--v-theme-on-surface), 0.4);
    }

    .trigger.download-invoice {
      display: flex;
      justify-content: center;
      // font-size: $text-size-sm;

      .v-icon {
        font-size: $text-size-lg;
        // margin: 0 6px 0 0;
      }

      &:hover {
        color: #f57c00;

        .v-icon {
          color: #f57c00;
        }
      }
    }

    .v-datatable {
      min-width: 800px;
    }
  }
</style>
